/**
 * magnetisme — l'accroche d'un calque aux repères, au cadre et aux autres calques.
 *
 * Sorti de `LabelCanvas` pour être mesurable sans navigateur (cf.
 * `banc/banc-magnetisme.mjs`). Un seuil d'accroche ne se règle pas au jugé : trop
 * court, l'accroche passe pour absente ; trop long, elle devient collante et on ne
 * peut plus poser un objet PRÈS d'un repère sans s'y coller. Il faut tracer la
 * courbe « position visée → position obtenue » et la regarder.
 */

/**
 * Distance d'accroche, en pixels À L'ÉCRAN — indépendante du zoom.
 *
 * Elle valait 5 px. Le retour d'usage de septembre parlait de « difficulté à
 * positionner une forme précisément sur les repères (manque de fluidité ou de
 * précision de l'accroche magnétique) » : à 5 px il faut viser l'accroche pour la
 * rencontrer, ce qui est exactement l'inverse de ce qu'on lui demande. 8 px est la
 * valeur des outils de PAO, et la planche du banc montre qu'à ce seuil les zones
 * mortes restent séparées aux zooms de travail.
 */
export const SEUIL_MAGNETISME_PX = 8;

/**
 * Cibles d'accroche portées par le cadre de l'étiquette, sur un axe : les deux
 * bords du format fini, son milieu, et les deux marges de sécurité.
 *
 * Le cadre n'était PAS une cible : seuls les deux axes médians l'étaient, et
 * encore, à la condition que les repères soient affichés. Coller un calque au bord
 * du format — le geste le plus courant — se faisait donc à la main. Le cadre existe
 * indépendamment de l'affichage des repères : c'est le document lui-même, pas une
 * aide qu'on montre ou qu'on cache.
 *
 * Les coordonnées des calques sont comptées depuis le bord du FORMAT FINI, le fond
 * perdu vivant en dehors (cf. `el.x + bleed` au rendu) : 0 et la dimension du
 * canevas sont donc bien les bords, sans correction.
 */
export function ciblesDuCadre(dimension, marge) {
    const d = Number(dimension) || 0;
    if (d <= 0) return [];
    const cibles = [0, d, d / 2];
    const m = Number(marge) || 0;
    if (m > 0 && m * 2 < d) cibles.push(m, d - m);
    return cibles;
}

/**
 * Accroche un calque sur un axe.
 *
 * Trois points du calque peuvent accrocher — son bord de début, son bord de fin et
 * son milieu — et c'est la paire (point, cible) la PLUS PROCHE qui gagne, pour que
 * l'accroche ne saute pas d'une cible à l'autre au passage d'une frontière.
 *
 * @param {number} position  position visée du bord de début
 * @param {number} taille    taille du calque sur cet axe
 * @param {number[]} cibles  coordonnées d'accroche
 * @param {number} seuil     distance d'accroche, dans l'unité du document
 * @returns {{position:number, ligne:number|null}} la position retenue, et la cible
 *          atteinte (pour tracer le repère bleu) — `ligne` vaut null sans accroche.
 */
export function accrocher(position, taille, cibles, seuil) {
    let meilleureDistance = Infinity;
    let meilleurePosition = position;
    let ligne = null;
    const points = [
        { valeur: position, correction: 0 },
        { valeur: position + taille, correction: taille },
        { valeur: position + taille / 2, correction: taille / 2 },
    ];
    for (const cible of cibles) {
        if (!Number.isFinite(cible)) continue;
        for (const point of points) {
            const distance = Math.abs(point.valeur - cible);
            if (distance < seuil && distance < meilleureDistance) {
                meilleureDistance = distance;
                meilleurePosition = cible - point.correction;
                ligne = cible;
            }
        }
    }
    return { position: meilleurePosition, ligne };
}
